import { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';

import { fetchNotificationDetail } from 'services/eventNotifications.service';

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** If body is not HTML, wrap escaped plain text for safe display. */
function notificationBodyHtml(body: string): string {
  const t = body.trim();
  if (!t) {
    return '<p></p>';
  }
  if (t.startsWith('<')) {
    return t;
  }
  return `<p style="white-space:pre-wrap">${escapeHtml(t)}</p>`;
}

type Props = {
  notificationId: string | null;
  open: boolean;
  onClose: () => void;
};

export default function NotificationDetailModal({ notificationId, open, onClose }: Props) {
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !notificationId) {
      setSubject('');
      setHtml('');
      setErr(null);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const d = await fetchNotificationDetail(notificationId);
        if (cancelled) {
          return;
        }
        setSubject(d?.subject ?? 'Notification');
        setHtml(notificationBodyHtml(d?.bodyHtml ?? ''));
      } catch {
        if (!cancelled) {
          setErr('Could not load this notification.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, notificationId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>{subject}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : err ? (
          <Typography color="error">{err}</Typography>
        ) : (
          <Box
            className="notification-email-body"
            sx={{
              '& img': { maxWidth: '100%', height: 'auto' },
              '& table': { maxWidth: '100%' },
              fontFamily: 'inherit',
              fontSize: '0.875rem'
            }}
            dangerouslySetInnerHTML={{ __html: html }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
